import { useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Zap, CheckCircle } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { LanguageSwitch } from '../../i18n/LanguageSwitch'
import { useLocale } from '../../i18n/LocaleProvider'
import { buildConfirmEmailPath, normalizeEmail } from '../../lib/emailVerification'
import { EmailVerificationForm } from './EmailVerificationForm'

export function ConfirmEmail() {
  const navigate = useNavigate()
  const { t } = useLocale()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const initialEmail = useMemo(
    () => normalizeEmail(location.state?.email || searchParams.get('email')),
    [location.state, searchParams]
  )
  const [email, setEmail] = useState(initialEmail)
  const [confirmed, setConfirmed] = useState(false)

  async function handleConfirmed(verifiedEmail) {
    setEmail(verifiedEmail)
    setConfirmed(true)
    window.history.replaceState({}, '', buildConfirmEmailPath(verifiedEmail))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-4 flex justify-end">
          <LanguageSwitch />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-12 h-12 rounded-2xl bg-brand-600 flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-900 text-xl leading-tight">WorkflowPro</p>
              <p className="text-gray-500 text-sm leading-tight">{t('auth.brandTagline')}</p>
            </div>
          </div>
        </div>
        <div className="card p-8">
          {confirmed ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.verification.successTitle')}</h1>
              <p className="text-gray-500 mb-6">
                {t('auth.verification.successBody')}
              </p>
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(`/signin?verified=true&email=${encodeURIComponent(email)}`)}
              >
                {t('auth.common.signIn')}
              </Button>
            </div>
          ) : (
            <EmailVerificationForm
              email={email}
              onEmailChange={(value) => setEmail(normalizeEmail(value))}
              onConfirmed={handleConfirmed}
              submitLabel={t('auth.verification.verifyEmail')}
              allowEmailEdit
            />
          )}
        </div>
      </div>
    </div>
  )
}
